/**
 * Session language for voice replies.
 *
 * Follow the language of each user utterance. An explicit command to speak a
 * named language selects that language instead. SPEAK_EXACTLY turns are ignored.
 */

export type VoiceLang = "en" | "nl" | "fa" | "zh";

export type VoiceCopy = { en: string; nl: string; fa: string; zh: string };

const PERSIAN_SCRIPT = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
const CHINESE_SCRIPT = /[\u4e00-\u9fff]/;

const PERSIAN_WORDS = [
  "پاشنه", "پنجه", "ضخامت", "قوس", "کفی", "ارتفاع", "عمق", "طول", "عرض",
  "چپ", "راست", "روشن", "خاموش", "بگذار", "بده", "نشان", "زوم", "برش",
  "گوه", "پرشدگی", "آینه", "برگرد", "هردو", "کفش", "سایز", "حکاکی",
  "میلی‌متر", "میلیمتر", "اینچ", "متریک", "تاریک", "برچسب", "رنگ",
  "پایین", "بالا", "داخل", "خارج", "ببند", "باز", "پا",
];

/** Dutch-only words. Never put English cognates here. */
const DUTCH_ONLY = [
  "hiel", "hak", "tenen", "voorvoet", "bovenkant", "onderkant", "mediaal",
  "lateraal", "inzoomen", "uitzoomen", "dichterbij", "verschuif", "omhoog",
  "omlaag", "terugzetten", "beginstand", "hielhoogte", "hieldiepte", "dikte",
  "voetboog", "lengte", "breedte", "etiketten", "kleur", "perspectief",
  "orthografisch", "weergave", "draadmodel", "knippen", "doorsnede", "raster",
  "voeten", "automatisch", "bewerkingspaneel", "oppervlak", "toevoegingen",
  "graveren", "projectinstellingen", "sluiten", "ongedaan", "spiegel",
  "ontwerp", "patiënt", "metrisch", "imperiaal", "donker", "instellingen",
  "zool", "zolen", "schoenmaat", "beide", "allebei", "procent", "graden",
  "toon", "verberg", "printen", "annuleren", "verzenden", "helling",
  "ondersteuning", "standaardprinter", "hielvulling", "voetboogvulling",
  "hielwig", "voorvoetwig", "zet", "alsjeblieft", "alstublieft", "spreek",
  "nederlands", "antwoord", "schakel", "naar", "de", "het", "een", "ik", "wil",
  "kun", "kan", "je", "jij", "van", "voor",
];

/** Common English command and function words. */
const ENGLISH_ONLY = [
  "the", "please", "this", "that", "these", "those", "what", "how", "why",
  "increase", "decrease", "show", "hide", "inches", "inch", "millimeters",
  "millimetres", "coloring", "clipping", "labels", "speak", "english", "answer",
  "respond", "switch", "language", "open", "close", "can", "you", "want",
  "need", "make", "change", "look",
];

/** Extra marker hits a tentative Latin partial needs before it may switch. */
const TENTATIVE_MARGIN = 2;

let sessionLang: VoiceLang = "en";

/**
 * When set, transcripts stop changing the session language.
 *
 * The exhibition demonstration is run in English, but its microphone is open
 * throughout, so room noise and the agent's own voice coming back through the
 * speakers were being transcribed and switching the language mid-run — visible
 * as the parameter step, the one step whose words come from this layer rather
 * than from the English-only script, suddenly speaking another language.
 */
let langLocked = false;

/** Pin the session language, ignoring what the transcriber reports. */
export function lockVoiceLang(lang: VoiceLang): void {
  sessionLang = lang;
  langLocked = true;
}

/** Let transcripts choose the language again. */
export function unlockVoiceLang(): void {
  langLocked = false;
}

export function currentVoiceLang(): VoiceLang {
  return sessionLang;
}

export function resetVoiceLang(): void {
  sessionLang = "en";
  langLocked = false;
}

function tokenizeLatin(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z\u00c0-\u024f]+/)
    .filter((w) => w.length >= 2);
}

function hitCount(tokens: string[], markers: string[]): number {
  const set = new Set(tokens);
  let n = 0;
  for (const marker of markers) {
    if (set.has(marker)) n += 1;
  }
  return n;
}

function hasPersianWords(text: string): boolean {
  const spaced = text.replace(/ي/g, "ی").replace(/ك/g, "ک");
  return PERSIAN_WORDS.some((word) => spaced.includes(word));
}

function detectScriptVoiceLang(text: string): "fa" | "zh" | null {
  if (PERSIAN_SCRIPT.test(text) || hasPersianWords(text)) return "fa";
  if (CHINESE_SCRIPT.test(text)) return "zh";
  return null;
}

const LANGUAGE_NAMES: Record<VoiceLang, string[]> = {
  en: ["english", "انگلیسی", "英语", "engels"],
  nl: ["dutch", "nederlands", "هلندی", "荷兰语"],
  fa: ["persian", "farsi", "فارسی", "波斯语"],
  zh: ["chinese", "mandarin", "چینی", "中文", "汉语", "chinees"],
};

/**
 * The requested target only wins for an explicit imperative. A capability
 * question such as "می‌توانی چینی حرف بزنی؟" intentionally does not match.
 */
function detectRequestedVoiceLang(text: string): VoiceLang | null {
  const normalized = text.toLowerCase().replace(/[؟?!.،,]/g, " ").replace(/\s+/g, " ").trim();
  const imperative =
    /(?:حرف بزن|صحبت کن|جواب بده|پاسخ بده)(?![\u0600-\u06FF])/.test(normalized) ||
    /^(?:please )?(?:speak|switch to|answer in|respond in)\b/.test(normalized) ||
    /^(?:spreek|antwoord in|schakel over naar)\b/.test(normalized) ||
    /(?:请用|改用|切换到).*(?:说|回答|语言)/.test(normalized);
  if (!imperative) return null;

  for (const lang of Object.keys(LANGUAGE_NAMES) as VoiceLang[]) {
    if (LANGUAGE_NAMES[lang].some((name) => normalized.includes(name))) return lang;
  }
  return null;
}

/**
 * Language of this utterance, or null when the words carry no evidence at all
 * and the previous language should keep being spoken.
 */
export function detectVoiceLang(text: string): VoiceLang | null {
  const raw = text.trim();
  if (!raw) return null;
  const requestedLang = detectRequestedVoiceLang(raw);
  if (requestedLang) return requestedLang;
  const scriptLang = detectScriptVoiceLang(raw);
  if (scriptLang) return scriptLang;

  const tokens = tokenizeLatin(raw);
  if (!tokens.length) return null;

  const dutchHits = hitCount(tokens, DUTCH_ONLY);
  const englishHits = hitCount(tokens, ENGLISH_ONLY);

  if (dutchHits > englishHits) return "nl";
  if (englishHits > dutchHits) return "en";
  return null;
}

export function noteUserSpeech(text: string): void {
  if (langLocked) return;
  const raw = text.trim();
  if (!raw) return;
  if (/^speak_exactly:/i.test(raw)) return;
  const detected = detectVoiceLang(raw);
  if (!detected) return;
  sessionLang = detected;
}

/**
 * Tentative ASR text lands while the user is still speaking, which is what puts
 * the language in place before the agent's tool call asks for a spoken result.
 *
 * Partials get rewritten as the recognizer settles, so a Latin partial only
 * switches on a clear margin. Persian and Chinese script is unambiguous from the
 * first character and switches straight away.
 */
export function noteTentativeUserSpeech(text: string): void {
  if (langLocked) return;
  const raw = text.trim();
  if (!raw) return;
  if (/^speak_exactly:/i.test(raw)) return;

  const scriptLang = detectScriptVoiceLang(raw);
  if (scriptLang) {
    sessionLang = scriptLang;
    return;
  }

  const tokens = tokenizeLatin(raw);
  if (!tokens.length) return;
  const dutchHits = hitCount(tokens, DUTCH_ONLY);
  const englishHits = hitCount(tokens, ENGLISH_ONLY);
  if (dutchHits >= englishHits + TENTATIVE_MARGIN) sessionLang = "nl";
  else if (englishHits >= dutchHits + TENTATIVE_MARGIN) sessionLang = "en";
}

export function voiceText(parts: VoiceCopy): string {
  return parts[sessionLang];
}

const FA_ONES = ["صفر", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"];
const FA_TEENS = [
  "ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده",
  "شانزده", "هفده", "هجده", "نوزده",
];
const FA_TENS = ["", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"];
const FA_HUNDREDS = [
  "", "صد", "دویست", "سیصد", "چهارصد", "پانصد",
  "ششصد", "هفتصد", "هشتصد", "نهصد",
];

const NL_ONES = ["nul", "een", "twee", "drie", "vier", "vijf", "zes", "zeven", "acht", "negen"];
const NL_TEENS = [
  "tien", "elf", "twaalf", "dertien", "veertien", "vijftien",
  "zestien", "zeventien", "achttien", "negentien",
];
const NL_TENS = ["", "", "twintig", "dertig", "veertig", "vijftig", "zestig", "zeventig", "tachtig", "negentig"];

const ZH_DIGITS = ["零", "一", "二", "三", "四", "五", "六", "七", "八", "九"];

function persianIntWords(n: number): string {
  if (n < 0) return `منفی ${persianIntWords(-n)}`;
  if (n < 10) return FA_ONES[n];
  if (n < 20) return FA_TEENS[n - 10];
  if (n < 100) {
    const ten = Math.floor(n / 10);
    const one = n % 10;
    return one ? `${FA_TENS[ten]} و ${FA_ONES[one]}` : FA_TENS[ten];
  }
  if (n < 1000) {
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    return rest ? `${FA_HUNDREDS[hundred]} و ${persianIntWords(rest)}` : FA_HUNDREDS[hundred];
  }
  const thousands = Math.floor(n / 1000);
  const rest = n % 1000;
  const head = thousands === 1 ? "هزار" : `${persianIntWords(thousands)} هزار`;
  return rest ? `${head} و ${persianIntWords(rest)}` : head;
}

function dutchIntWords(n: number): string {
  if (n < 0) return `min ${dutchIntWords(-n)}`;
  if (n < 10) return NL_ONES[n];
  if (n < 20) return NL_TEENS[n - 10];
  if (n < 100) {
    const ten = Math.floor(n / 10);
    const one = n % 10;
    if (!one) return NL_TENS[ten];
    const glue = NL_ONES[one].endsWith("e") ? "ën" : "en";
    return `${NL_ONES[one]}${glue}${NL_TENS[ten]}`;
  }
  if (n < 1000) {
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    const head = hundred === 1 ? "honderd" : `${NL_ONES[hundred]}honderd`;
    return rest ? `${head}${dutchIntWords(rest)}` : head;
  }
  const thousands = Math.floor(n / 1000);
  const rest = n % 1000;
  const head = thousands === 1 ? "duizend" : `${dutchIntWords(thousands)}duizend`;
  return rest ? `${head}${dutchIntWords(rest)}` : head;
}

function chineseIntWords(n: number): string {
  if (n < 0) return `负${chineseIntWords(-n)}`;
  if (n === 0) return "零";
  if (n < 10) return ZH_DIGITS[n];
  if (n < 20) return n === 10 ? "十" : `十${ZH_DIGITS[n % 10]}`;
  if (n < 100) {
    const ten = Math.floor(n / 10);
    const one = n % 10;
    return one ? `${ZH_DIGITS[ten]}十${ZH_DIGITS[one]}` : `${ZH_DIGITS[ten]}十`;
  }
  if (n < 1000) {
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    const head = `${ZH_DIGITS[hundred]}百`;
    if (!rest) return head;
    if (rest < 10) return `${head}零${ZH_DIGITS[rest]}`;
    if (rest < 20) return `${head}一${chineseIntWords(rest)}`;
    return `${head}${chineseIntWords(rest)}`;
  }
  const thousands = Math.floor(n / 1000);
  const rest = n % 1000;
  const head = thousands === 1 ? "一千" : `${chineseIntWords(thousands)}千`;
  if (!rest) return head;
  if (rest < 100) return `${head}零${chineseIntWords(rest)}`;
  return `${head}${chineseIntWords(rest)}`;
}

function splitScaled(value: number, decimals: number): { sign: string; whole: number; fracPart: number; scale: number } {
  const signNeg = value < 0;
  const abs = Math.abs(value);
  const scale = Math.pow(10, Math.max(0, decimals));
  const scaled = Math.round(abs * scale) / scale;
  const whole = Math.floor(scaled + 1e-9);
  return { sign: signNeg ? "-" : "", whole, fracPart: scaled - whole, scale };
}

/**
 * How a number should be spoken. Persian, Dutch, and Chinese are written in
 * words so the voice cannot fall back to English "twelve".
 */
export function spokenNumber(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return String(value);
  if (sessionLang === "en") {
    const rounded = Number(value.toFixed(decimals));
    return String(Number.isInteger(rounded) ? rounded : rounded);
  }

  const { sign, whole, fracPart, scale } = splitScaled(value, decimals);
  if (sessionLang === "nl") {
    const prefix = sign ? "min " : "";
    if (fracPart < 1 / scale) return prefix + dutchIntWords(whole);
    if (Math.abs(fracPart - 0.5) < 1e-9) {
      return whole ? `${prefix}${dutchIntWords(whole)} en een half` : `${prefix}een half`;
    }
    const frac = Math.round(fracPart * scale);
    return `${prefix}${dutchIntWords(whole)} komma ${dutchIntWords(frac)}`;
  }
  if (sessionLang === "zh") {
    const prefix = sign ? "负" : "";
    if (fracPart < 1 / scale) return prefix + chineseIntWords(whole);
    if (Math.abs(fracPart - 0.5) < 1e-9) {
      return whole ? `${prefix}${chineseIntWords(whole)}点五` : `${prefix}零点五`;
    }
    const frac = Math.round(fracPart * scale);
    return `${prefix}${chineseIntWords(whole)}点${chineseIntWords(frac)}`;
  }

  const prefix = sign ? "منفی " : "";
  if (fracPart < 1 / scale) return prefix + persianIntWords(whole);
  if (Math.abs(fracPart - 0.5) < 1e-9) {
    return whole ? `${prefix}${persianIntWords(whole)} و نیم` : `${prefix}نیم`;
  }
  const frac = Math.round(fracPart * scale);
  return `${prefix}${persianIntWords(whole)} ممیز ${persianIntWords(frac)}`;
}
